import store from "../../store/store";
import Button from "../common/Button";
import { gameActions } from "./gameSlice";
import { formatMsDuration, formatPlural } from "../../utils/formatting";
import PlayerPicture from "../Players/PlayerPicture";
import Players from "../Players/Players";

function GameResults() {
  const gameState = store.getState().game;
  const winners = gameState.winners;
  const stats = gameState.stats;
  const avatarSize = 45;

  const isHost = gameState.hostId === gameState.player?.data.playerId;
  const isAdmin = store.getState().gameList.admins.includes(gameState.player?.data.playerId ?? '');

  function newGame() {
    // only admins can keep notifications disabled across a restart, everyone else always restarts with notifications on
    const disableNotifications = isAdmin && !gameState.settings.notifyDiscord && !gameState.settings.notifyTelegram;
    store.dispatch(gameActions.sendRestart({
      gameId: gameState.gameId,
      playerId: gameState.player?.data.playerId,
      disableNotifications: disableNotifications
    }));
  }

  function sendEndSession() {
    if (gameState.player) {
      store.dispatch(gameActions.sendEndSession({
        adminId: gameState.player?.data.playerId,
        gameId: gameState.gameId,
      }));
    }
  }


  if (!stats) {
    return (
      <div className="game-results_error">error: no stats, no buttons</div>
    );
  }

  return (
    <div className="game-results">

      <Players />

      {isHost &&
        <div className="game-results_buttons">
          <Button
            text={'New game'}
            onClick={() => newGame()}
            disabled={!isHost}
            confirmPrompt={'Are you sure you want to start a new game?'}
          />
          <Button
            text={'End session'}
            onClick={sendEndSession}
            disabled={!isHost}
            classes="danger"
            confirmPrompt={'Are you sure you want to end this session?'}
          />
        </div>
      }

      {stats.durationMs &&
        <div className="game-results_row">
          <div className="game-results_col game-results_col-right">Game Duration: </div>
          <div className="game-results_col">
            <span className="game-results_highlight">{formatMsDuration(stats.durationMs)}</span>
          </div>
        </div>
      }

      <div className="game-results_row">
        <div className="game-results_col game-results_col-right">Cards played: </div>
        <div className="game-results_col">
          <span className="game-results_highlight">{gameState.playedSize}</span>
        </div>
      </div>

      <div className="game-results_row">
        <div className="game-results_col game-results_col-right">{formatPlural(winners, 'Winner', 'Winners')}:</div>
        <div className="game-results_col">
          {stats.winnerPlayers.map(it =>
            <div className="game-results_player-picture" key={it.playerId}>
              <PlayerPicture player={it} size={avatarSize} />
            </div>
          )}
        </div>
      </div>

      <div className="game-results_row game-results_row_empty"></div>

      {stats.perfectStorytellers.length > 0 &&
        <div className="game-results_row">
          <div className="game-results_col game-results_col-right">
            <div className="game-results_col-content" title="Players who did not fail during their storytelling turns">
              {formatPlural(stats.perfectStorytellers, 'Perfect storyteller', 'Perfect storytellers')}:
            </div>
          </div>
          <div className="game-results_col">
            {stats.perfectStorytellers.map(it =>
              <div className="game-results_player-picture" key={it.playerId}>
                <PlayerPicture player={it} size={avatarSize} />
              </div>
            )}
          </div>
        </div>
      }

      {stats.onlyGuessers.length > 0 &&
        <div className="game-results_row">
          <div className="game-results_col game-results_col-right">
            <div className="game-results_col-content" title="Players who were the only ones to guess a sentence">
              {formatPlural(stats.onlyGuessers, 'Only guesser', 'Only guessers')}:
            </div>
          </div>
          <div className="game-results_col">
            {stats.onlyGuessers.map(it =>
              <div className="game-results_player-picture" key={it.playerId}>
                <PlayerPicture player={it} size={avatarSize} />
              </div>
            )}
          </div>
        </div>
      }

      {stats.maxScoreTurnPlayers.length > 0 &&
        <div className="game-results_row">
          <div className="game-results_col game-results_col-right">
            <div className="game-results_col-content" title="Players who got maximum possible score in a turn">
              {formatPlural(stats.maxScoreTurnPlayers, 'Max score player', 'Max score players')}:
            </div>
          </div>
          <div className="game-results_col">
            {stats.maxScoreTurnPlayers.map(it =>
              <div className="game-results_player-picture" key={it.playerId}>
                <PlayerPicture player={it} size={avatarSize} />
              </div>
            )}
          </div>
        </div>
      }

      {stats.bestGuessPlayers.length > 0 &&
        <div className="game-results_row">
          <div className="game-results_col game-results_col-right">
            <div className="game-results_col-content" title="Players who guessed the most sentences">
              {formatPlural(stats.bestGuessPlayers, 'Best Guesser', 'Best Guessers')}
              <span className="game-results_highlight"> [{stats.maxGuessScore}]</span>:
            </div>
          </div>
          <div className="game-results_col">
            {stats.bestGuessPlayers.map(it =>
              <div className="game-results_player-picture" key={it.playerId}>
                <PlayerPicture player={it} size={avatarSize} />
              </div>
            )}
          </div>
        </div>
      }

      {stats.bestMatchPlayers.length > 0 &&
        <div className="game-results_row">
          <div className="game-results_col game-results_col-right">
            <div className="game-results_col-content" title="Players who matched the most">
              {formatPlural(stats.bestMatchPlayers, 'Best Matcher', 'Best Matchers')}
              <span className="game-results_highlight"> [{stats.maxMatchScore}]</span>:
            </div>
          </div>
          <div className="game-results_col">
            {stats.bestMatchPlayers.map(it =>
              <div className="game-results_player-picture" key={it.playerId}>
                <PlayerPicture player={it} size={avatarSize} />
              </div>
            )}
          </div>
        </div>
      }

      <div className="game-results_row">
        <div className="game-results_col game-results_col-right">
          <div className="game-results_col-content" title="Players who received the most amount of votes form storyteller">
            {formatPlural(stats.bestStorytellersVote, 'Most voted by storyteller')}
            <span className="game-results_highlight"> [{stats.maxStorytellerVote}]</span>:
          </div>
        </div>
        <div className="game-results_col">
          {stats.bestStorytellersVote.map(it =>
            <div className="game-results_col_list-row" key={it.playerId}>
              <div className="game-results_player-picture" ><PlayerPicture player={it} size={avatarSize} /></div>
            </div>
          )}
        </div>
      </div>

      {stats.bestStorytellers.length > 0 && stats.perfectStorytellers.length === 0 &&
        <div className="game-results_row">
          <div className="game-results_col game-results_col-right">
            <div className="game-results_col-content" title="Players who got the most stores by storytelling">
              {formatPlural(stats.bestStorytellers, 'Best storyteller score')}
              <span className="game-results_highlight"> [{stats.maxStorytellerScore}]</span>:
            </div>
          </div>
          <div className="game-results_col">
            {stats.bestStorytellers.map(it =>
              <div className="game-results_col_list-row" key={it.playerId}>
                <div className="game-results_player-picture"><PlayerPicture player={it} size={avatarSize} /></div>
              </div>
            )}
          </div>
        </div>
      }


      {stats.minWaitingForPlayers.length > 0 &&
        <div className="game-results_row">
          <div className="game-results_col game-results_col-right">
            <div className="game-results_col-content" title="Players who were minimally waited for">
              {formatPlural(stats.minWaitingForPlayers, 'Min waited for')}
              <span className="game-results_highlight"> [{stats.minWaitingFor}]</span>:
            </div>
          </div>
          <div className="game-results_col">
            {stats.minWaitingForPlayers.map(it =>
              <div className="game-results_player-picture" key={it.playerId}>
                <PlayerPicture player={it} size={avatarSize} />
              </div>
            )}
          </div>
        </div>
      }

      {stats.maxWaitingForPlayers.length > 0 &&
        <div className="game-results_row">
          <div className="game-results_col game-results_col-right">
            <div className="game-results_col-content" title="Players who were waited for the most">
              {formatPlural(stats.maxWaitingForPlayers, 'Max waited for')}
              <span className="game-results_highlight"> [{stats.maxWaitingFor}]</span>:
            </div>
          </div>
          <div className="game-results_col">
            {stats.maxWaitingForPlayers.map(it =>
              <div className="game-results_player-picture" key={it.playerId}>
                <PlayerPicture player={it} size={avatarSize} />
              </div>
            )}
          </div>
        </div>
      }

      <div className="game-results_row game-results_row_empty"></div>

      {stats.ratedSentences && stats.ratedSentences.length > 0 &&
        <div className="game-results_row">
          <div className="game-results_col game-results_col-right">Rated Sentences: </div>
          <div className="game-results_col game-results_col_list-column">{stats.ratedSentences.map((it, i) =>
            <div key={i}>{it}</div>
          )}</div>
        </div>
      }

    </div>
  )
}

export default GameResults;